// @flow
import type {Element} from 'react'
import React, {useEffect} from 'react'
import CreatableSelect from 'react-select/creatable'
import ReactSelect from 'react-select'
import {initTooltipDelegation, hideActiveTooltip} from '../tooltipManager'
import useSelectEvents from '../useSelectEvents'
import useOptionFormatting from '../useOptionFormatting'
import useCustomFilterOption from '../useCustomFilterOption'
import type {Option, PlaceholderData} from '../types'

export type Props = $ReadOnly<{
  defaultValue?: Object,
  id: string,
  isMulti?: boolean,
  isSearchable?: boolean,
  isClearable?: boolean,
  name: string,
  onChange?: (newValue: Option | Array<Option>, Object) => void,
  onInit?: (selectWrapper: Object) => void,
  options: Array<Option>,
  selectType?: string,
  showSubtitle?: boolean,
  placeholderData?: PlaceholderData,
  wrapperClass?: string,
  iconOnlySelection?: boolean,
}>;

function StaticRailsSelect({
  defaultValue, id, isMulti,
  isSearchable, isClearable, name,
  options, selectType = 'default', showSubtitle, placeholderData,
  wrapperClass, iconOnlySelection, onInit, onChange,
}: Props): Element<'div'> {
  // --- memoize the component map & chosen component ---
  const selectComponents = React.useMemo(
    () => ({
      default: ReactSelect,
      creatable: CreatableSelect,
    }),
    [],
  )

  const ComponentType = React.useMemo(
    () => selectComponents[selectType] || ReactSelect,
    [selectComponents, selectType],
  )

  const {handleChange, wrapperRef, selectRef, setMenuIsOpen}
    = useSelectEvents({onInit, onChange})

  const {formatOptionLabel, components} = useOptionFormatting(options, {
    iconOnlySelection,
    showSubtitle,
    placeholderData,
  })

  const {filterOption} = useCustomFilterOption()

  // --- memoize small inline handlers & style objects so identities are stable ---
  const handleMenuOpen = React.useCallback(() => {
    hideActiveTooltip()
    setMenuIsOpen(true)
  }, [setMenuIsOpen])
  const handleMenuClose = React.useCallback(
    () => setMenuIsOpen(false),
    [setMenuIsOpen],
  )

  // Optional: stable styles object keeps React-Select from re-rendering
  const selectStyles = React.useMemo(
    () => ({
      // applies when menu is portaled
      menuPortal: (base) => ({...base, zIndex: 9999}),
      // applies when menu is rendered in-place (no portal)
      menu: (base) => ({...base, zIndex: 9999}),
      multiValueRemove: (base, state) => ({
        ...base,
        background: state.isFocused ? 'rgba(0,0,0,0.05)' : 'transparent',
        // also include explicit :hover in case the library applies it
        ':hover': {
          background: 'rgba(0,0,0,0.05)',
        },
      }),
    }),
    [],
  )

  // Stable value/label getters avoid full re-renders when labels mutate elsewhere
  const getOptionValue = React.useCallback((o) => String(o.value), [])
  const getOptionLabel = React.useCallback((o) => String(o.label), [])

  useEffect(() => {
    initTooltipDelegation()
  }, [])

  return (
    <div
      ref={wrapperRef}
      className={wrapperClass}
      test-id={`RailsSelect-${name}`}
    >
      <ComponentType
        defaultValue={defaultValue}
        id={id}
        isMulti={isMulti}
        isSearchable={isSearchable}
        isClearable={isClearable}
        name={name}
        onChange={handleChange}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        options={options}
        placeholder={placeholderData}
        classNamePrefix="rails_select"
        formatOptionLabel={formatOptionLabel}
        filterOption={filterOption}
        components={components}
        ref={selectRef}
        // small perf helpers
        getOptionValue={getOptionValue}
        getOptionLabel={getOptionLabel}
        styles={selectStyles}
        // defaultMenuIsOpen // Uncomment to open the dropdown by default to inspect things in console more easily
      />
    </div>
  )
}

export default StaticRailsSelect
